'use client'

import React, { useState, useEffect } from 'react'
import { useRouter } from 'next/navigation'
import Slider from 'react-slick'
import Image from 'next/image'
import Link from 'next/link'
import { Button } from 'antd'
import RectRedSVG from '@/public/icons/rect-red.svg'
import PrevArrowSVG from '@/public/icons/prevArrow.svg'
import NextArrowSVG from '@/public/icons/nextArrow.svg'
import RightArrowSVG from '@/public/icons/rightArrow.svg'
import AddCartButton from '@/components/AddCartButton'
import WishButton from '@/components/WishButton'
import { useUserStore } from '@/stores/user-store-provider'
import { formatDiscountNum } from '@/utils/index'
import CurrencyPrice from '@/components/CurrencyPrice'
import SingleDiscountCoverSvg from '@/public/icons/singleDiscountCover.svg'
import SingleDiscountCoverMobileSvg from '@/public/icons/singleDiscountCover-mobile.svg'
import MultiDiscountCoverSvg from '@/public/icons/multiDiscountCover.svg'
import MultiDiscountCoverMobileSvg from '@/public/icons/multiDiscountCover-mobile.svg'

import 'slick-carousel/slick/slick.css'
import 'slick-carousel/slick/slick-theme.css'

interface BookSlickProps {
  lng: string
  categoryId?: number
  type: string
  listName: string
  autoPlay?: boolean
  listData: any
  arrows?: boolean
  moreText: string
  preOrderText: string
}

export function BookSlick({ lng, categoryId, type, listName, autoPlay = false, listData, arrows = false, moreText, preOrderText }: BookSlickProps) {
  const { currency, currencySign, exchangeRate } = useUserStore((state) => state)
  const [screenWidth, setScreenWidth] = useState(0)
  const router = useRouter()

  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth)
    }
    handleResize()
    window.addEventListener('resize', handleResize)

    return () => window.removeEventListener('resize', handleResize)
  }, [])

  let showMore = true
  let activeUrl = ''
  if (type === 'booksetAndBookmenu') {
    activeUrl = `/${lng}/curated-collections`
  } else if (type === 'bookmenu') {
    activeUrl = `/${lng}/bookmenu/${categoryId}`
  } else if (type === 'editorRecommend') {
    showMore = false
  } else if (type === 'category') {
    activeUrl = `/${lng}/all-books/category/${categoryId}`
  }

  // 根据屏幕宽度计算合适的slidesToShow值
  const getInitialSlidesToShow = (width: number, itemType: string) => {
    if (itemType === 'booksetAndBookmenu') {
      return width < 768 ? 2 : 3
    }

    if (width < 576) return 3
    if (width < 768) return 4
    if (width < 992) return 5
    if (width < 1170) return 6
    return 6
  }

  const showLength = getInitialSlidesToShow(screenWidth, type)

  // 为小屏幕设备添加rows配置
  const getInitialSettings = (width: number, slidesToShow: number) => {
    const baseSettings = {
      dots: false,
      infinite: true,
      arrows,
      swipeToSlide: true,
      slidesToShow,
      autoplay: autoPlay,
      autoplaySpeed: 2000,
      pauseOnHover: true,
      initialSlide: 0
    }

    // 小于576px时2行展示
    if (width < 576) {
      return { ...baseSettings, rows: listData.length > 3 ? 2 : 1 }
    }

    return baseSettings
  }

  const settingsOfBook = {
    ...getInitialSettings(screenWidth, showLength),
    prevArrow: <PrevArrowBook />,
    nextArrow: <NextArrow />,
    responsive: [
      {
        breakpoint: 1222,
        settings: {
          slidesToShow: 6,
          slidesToScroll: 1
        }
      },
      {
        breakpoint: 1170,
        settings: {
          slidesToShow: 6,
          slidesToScroll: 1
        }
      },
      {
        breakpoint: 992,
        settings: {
          slidesToShow: 5,
          slidesToScroll: 1
        }
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 4,
          slidesToScroll: 2,
          initialSlide: 2
        }
      },
      {
        breakpoint: 576,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 1,
          rows: listData.length > 3 ? 2 : 1
        }
      }
    ]
  }

  const settingsOfBooksetAndBookmenu = {
    ...getInitialSettings(screenWidth, showLength),
    prevArrow: <PrevArrowBooksetAndBookmenu />,
    nextArrow: <NextArrow />,
    responsive: [
      {
        breakpoint: 1222,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 1
        }
      },
      {
        breakpoint: 1170,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 1
        }
      },
      {
        breakpoint: 992,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 1
        }
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
          rows: 2
        }
      }
    ]
  }
  return (
    <>
      <div className="my-7 flex flex-nowrap items-center justify-between px-0 sm:px-3.75">
        <h2 className="flex items-center truncate text-[18px] sm:text-[24px]">
          <RectRedSVG className="mr-2.5 hidden sm:inline-block" />
          {listName}
        </h2>
        {showMore ? (
          <Link href={activeUrl} className="group flex cursor-pointer items-center truncate text-[12px] text-gray sm:text-[16px]">
            {moreText}
            <RightArrowSVG className="ml-1 text-gray group-hover:text-red" />
          </Link>
        ) : (
          <></>
        )}
      </div>
      {type !== 'booksetAndBookmenu' && (
        <div className={`slider-container`}>
          <Slider {...settingsOfBook} className={`${listData.length > 6 ? 'endao-slider' : 'endao-slider-less'} ${autoPlay ? 'isAutoPlay' : 'notAutoPlay'}`}>
            {listData.map((item: any, index: number) => (
              <div key={item.bookId} className="sliderItem mb-6 flex flex-col items-start sm:mb-0 sm:items-center">
                <div className="relative flex h-auto w-full flex-col items-center">
                  {/* {item.discountPercent && <div className="absolute right-0 top-0 px-2 py-1 text-white z-[999]">{`${lng === 'en' ? (100-item.discountPercent) : item.discountPercent}${lng === 'en' ? 'off' : '折'}`}</div>} */}
                  <div className="relative !block h-full w-full sm:max-w-[100px] md:max-w-[140px] lg:max-w-[150px] xl:max-w-[174px]">
                    {/* <Link href={`/${lng}/book/${item.bookId}`} className="relative !block h-full max-w-[174px]"> */}
                    <Image src={item.imgUrlFull} alt="book cover of editor recommend" className="cursor-pointer" width={174} height={258} sizes="100vw" onClick={() => router.push(`/${lng}/book/${item.bookId}`)} style={{ width: '100%', height: 'auto', objectFit: 'fill', borderRadius: '7px', boxShadow: '3px 6px 6px 0 rgba(47 56 68 / 12%)' }} />
                    {/* 单件折 */}
                    {!!item.discountPercent && !!item.priceDiscount && (
                      <div className="absolute right-0 top-0 z-[99] translate-x-[1px] translate-y-[-1px]">
                        <SingleDiscountCoverSvg className="hidden sm:block" />
                        <SingleDiscountCoverMobileSvg className="block sm:hidden" />
                        <div className={`absolute bottom-0 left-0 right-0 top-0 flex items-center justify-center pl-[7px] ${lng === 'en' ? 'pr-1' : ''} text-[11px] ${lng === 'en' ? 'sm:text-[12px]' : 'sm:text-[14px]'} text-white`}>
                          <span>{`${lng === 'en' ? 100 - item.discountPercent : formatDiscountNum(item.discountPercent)}`}</span>
                          <span className="hidden sm:inline">{`${lng === 'en' ? '% off' : '折'}`}</span>
                          <span className="inline sm:hidden">{`${lng === 'en' ? '%off' : '折'}`}</span>
                        </div>
                      </div>
                    )}
                    {/* N件折 */}
                    {!!item.discountIsValid && (
                      <div className="absolute right-0 top-0 z-[99] translate-x-[1px] translate-y-[-1px]">
                        <MultiDiscountCoverSvg className="hidden sm:block" />
                        <MultiDiscountCoverMobileSvg className="block sm:hidden" />
                        <div className="absolute bottom-0 left-0 right-0 top-0 flex items-center justify-center pl-[7px] text-[11px] text-white sm:text-[14px]">{`N件折`}</div>
                      </div>
                    )}
                    <div className="relative">
                      <h3 className="mt-3" title={item.name}>
                        <Link href={`/${lng}/book/${item.bookId}`} className="line-clamp-2 h-[32px] text-[13px] leading-[16px] sm:h-[56px] sm:text-[16px] sm:leading-[28px]">
                          {item.name}
                        </Link>
                      </h3>
                      {/* 预售 */}
                      {!!item.salesModel && <div className="absolute left-[8px] top-[-37px] z-[99] h-[20px] rounded-[20px] bg-[#000] px-1.25 text-center text-[11px] leading-[20px] text-white opacity-[0.8] sm:px-[7px] sm:text-[12px]">{preOrderText}</div>}
                    </div>
                    <p className="mt-1 hidden truncate text-[14px] sm:block">
                      {item?.author.split('#').map((author: string, index: number) => (
                        <Link key={index} href={`/${lng}/all-books/searchText/${author}`}>
                          {`${author} `}
                        </Link>
                      ))}
                    </p>
                    <p className="mt-0.5 text-[12px] text-red sm:text-[14px]">
                      <span>HK$</span>
                      <span className="!text-[5px]">&nbsp;</span>
                      <span>{item.priceDiscount}</span>
                      {!!item.price && <del className="ml-2 hidden font-thin text-gray md:inline-block">{`HK$${item.price}`}</del>}
                    </p>
                    <p className="text-[12px] text-red sm:text-[14px]">
                      <CurrencyPrice priceDiscount={item.priceDiscount} />
                    </p>
                    {/* </Link> */}
                    <div className="mt-5 hidden gap-x-[28px] sm:flex sm:justify-center">
                      <WishButton bookName={item.name} bookId={item.bookId} lng={lng} type={'circle'}></WishButton>
                      <AddCartButton lng={lng} bookName={item.name} bookId={item.bookId}></AddCartButton>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </Slider>
        </div>
      )}
      {type === 'booksetAndBookmenu' && (
        <div className={`slider-container pl-0 sm:pl-3.75`}>
          <Slider {...settingsOfBooksetAndBookmenu} className={`endao-slider ${autoPlay ? 'isAutoPlay' : 'notAutoPlay'}`}>
            {[...listData].map((item: any, index: number) => (
              <div key={item.categoryId} className="mb-6 flex flex-col items-center md:mb-3 ">
                <div className="mr-4 h-auto max-w-[132px] max-[1210px]:max-w-[371px] md:min-w-[152px] min-[1211px]:max-w-[386px] ">
                  <div className="flex h-auto w-full flex-col items-center rounded-tl-[7px] rounded-tr-[7px] shadow-[3px_0_6px_0_rgb(47,56,68,12%)]">
                    <Link href={`/${lng}/${item.type === 'set' ? 'bookset' : 'bookmenu'}/${item.categoryId}`} className="relative !block h-full w-full">
                      <Image src={item.bookCoverUrl} className="rounded-tl-[7px] rounded-tr-[7px]" alt="" width={500} height={374} sizes="100vw" style={{ width: '100%', height: 'auto', objectFit: 'fill' }} />
                    </Link>
                  </div>
                  <div className="h-auto w-full rounded-bl-[7px] rounded-br-[7px] px-1 py-2 shadow-[3px_6px_6px_0_rgb(47,56,68,12%)] sm:px-4.5 md:py-4">
                    <Link href={`/${lng}/${item.type === 'set' ? 'bookset' : 'bookmenu'}/${item.categoryId}`} title={lng === 'en' ? item.categoryNameEn : lng === 'zh-CN' ? item.categoryNameSc : item.categoryNameTc}>
                      <h2 className="truncate text-[14px] leading-[25px] hover:text-red md:text-[18px]">{lng === 'en' ? item.categoryNameEn : lng === 'zh-CN' ? item.categoryNameSc : item.categoryNameTc}</h2>
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </Slider>
        </div>
      )}
    </>
  )
}

function PrevArrowBook(props: any) {
  const { className, style, onClick } = props
  return <Button shape="circle" icon={<PrevArrowSVG className="relative left-[-1px] top-[2px] hover:!text-[#b2b2b2]" />} onClick={onClick} className="!hidden !h-[42px] !w-[42px] hover:!bg-[#f7f7f7] min-[1314px]:!block" style={{ ...style, position: 'absolute', top: '140px', left: '-40px' }} />
}

function PrevArrowBooksetAndBookmenu(props: any) {
  const { className, style, onClick } = props
  return <Button shape="circle" icon={<PrevArrowSVG className="relative left-[-1px] top-[2px] hover:!text-[#b2b2b2]" />} onClick={onClick} className="!hidden !h-[42px] !w-[42px] hover:!bg-[#f7f7f7] min-[1314px]:!block" style={{ ...style, position: 'absolute', top: '140px', left: '-53px' }} />
}

function NextArrow(props: any) {
  const { className, style, onClick } = props
  return <Button shape="circle" icon={<NextArrowSVG className="relative right-0 top-[2px] hover:!text-[#b2b2b2]" />} onClick={onClick} className="!hidden !h-[42px] !w-[42px] hover:!bg-[#f7f7f7] min-[1314px]:!block" style={{ ...style, position: 'absolute', top: '140px', right: '-42px' }} />
}
